import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Stage, Layer, Rect, Text as KonvaText, Image as KonvaImage, Circle, Line, Group, Transformer } from 'react-konva';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { UserMenu } from '../components/UserMenu';
import { toast } from 'sonner';
import { 
  Save, Type, Image, Square, Circle as CircleIcon, Minus, Trash2, Download, 
  Copy, Layers, Eye, EyeOff, Lock, Unlock, AlignLeft, AlignCenter, AlignRight,
  Bold, Italic, ZoomIn, ZoomOut, Grid3X3, Layout, FileText, Link2,
  DollarSign, MapPin, Bed, Bath, Ruler, LayoutList, CheckCircle2, Sparkles, Plus, UserCircle
} from 'lucide-react';
import useImage from 'use-image';
import { URLImage, ResizableShape, PlaceholderShape, NewsletterBlockShape, GridLines } from '../components/canvas';

export const CanvasDesignerPage = () => {
  const { templateId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const stageRef = useRef();
  const fileInputRef = useRef();
  
  // Read type from URL search params (e.g., /canvas-designer?type=story)
  const searchParams = new URLSearchParams(window.location.search);
  const initialType = searchParams.get('type') || 'post';
  const returnTo = searchParams.get('returnTo') || '/design-studio';
  
  // Template metadata
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [category, setCategory] = useState('new_listing');
  const [type, setType] = useState(initialType);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  
  // Canvas elements
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(0);
  
  // Multi-page support (print templates, carousels)
  const [pages, setPages] = useState([{ id: 'page_1', elements: [], backgroundColor: '#FFFFFF', backgroundImage: null }]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const isPrintType = ['postcard', 'brochure', 'flyer'].includes(type);
  const isMultiPage = isPrintType || type === 'carousel';

  // Page thumbnails for live preview
  const [pageThumbnails, setPageThumbnails] = useState({});

  // Postcard size options
  const [postcardSize, setPostcardSize] = useState('4x6');
  const [postcardCustomW, setPostcardCustomW] = useState(6); // inches
  const [postcardCustomH, setPostcardCustomH] = useState(4); // inches
  const POSTCARD_SIZES = {
    '4x6': { width: 900, height: 600, label: '4×6" (Standard)' },
    '5x7': { width: 1050, height: 750, label: '5×7" (Large)' },
    '6x9': { width: 1350, height: 900, label: '6×9" (Jumbo)' },
    '6x11': { width: 1650, height: 900, label: '6×11" (Panoramic)' },
    'custom': { width: null, height: null, label: 'Custom size…' },
  };
  // Convert inches → canvas pixels (150 DPI matches the rest of the print pipeline)
  const POSTCARD_DPI = 150;
  const inchesToPx = (inches) => Math.max(150, Math.round(inches * POSTCARD_DPI));
  
  // Canvas settings
  const [canvasWidth, setCanvasWidth] = useState(1080);
  const [canvasHeight, setCanvasHeight] = useState(1080);
  const [scale, setScale] = useState(0.4);
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(50);
  
  // Selected element properties
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#064E3B');
  const [selectedBgColor, setSelectedBgColor] = useState('#FFFFFF');
  const [fontSize, setFontSize] = useState(48);
  const [fontFamily, setFontFamily] = useState('Playfair Display');
  const [textAlign, setTextAlign] = useState('left');
  const [fontWeight, setFontWeight] = useState('normal');
  const [fontStyle, setFontStyle] = useState('normal');
  const [opacity, setOpacity] = useState(1);

  // Redirect non-admin users (admin OR owner allowed)
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'owner') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (type === 'reel') {
      setCanvasWidth(1080);
      setCanvasHeight(1920);
      setScale(0.35);
    } else if (type === 'post') {
      setCanvasWidth(1080);
      setCanvasHeight(1080);
      setScale(0.4);
    } else if (type === 'story') {
      setCanvasWidth(1080);
      setCanvasHeight(1920);
      setScale(0.35);
    } else if (type === 'newsletter') {
      setCanvasWidth(600);
      setCanvasHeight(800);
      setScale(0.6);
    } else if (type === 'flyer') {
      // 8.5x11 inches at 150 DPI = 1275x1650 pixels
      setCanvasWidth(1275);
      setCanvasHeight(1650);
      setScale(0.35);
    } else if (type === 'postcard') {
      const sz = POSTCARD_SIZES[postcardSize] || POSTCARD_SIZES['4x6'];
      setCanvasWidth(sz.width);
      setCanvasHeight(sz.height);
      setScale(0.5);
    } else if (type === 'brochure') {
      // Tri-fold brochure: 11x8.5 inches at 150 DPI = 1650x1275 pixels (landscape)
      setCanvasWidth(1650);
      setCanvasHeight(1275);
      setScale(0.35);
    } else if (type === 'carousel') {
      setCanvasWidth(1080);
      setCanvasHeight(1080);
      setScale(0.5);
    }
  }, [type, postcardSize]);

  // Page navigation for multi-page templates
  const switchToPage = (newIndex) => {
    if (newIndex === currentPageIndex || newIndex < 0 || newIndex >= pages.length) return;
    // Capture current page thumbnail before switching
    captureThumbnail();
    // Save current page
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = {
      ...updatedPages[currentPageIndex],
      elements: [...elements],
      backgroundColor: selectedBgColor,
    };
    setPages(updatedPages);
    // Load new page
    setCurrentPageIndex(newIndex);
    setElements(updatedPages[newIndex]?.elements || []);
    setSelectedBgColor(updatedPages[newIndex]?.backgroundColor || '#FFFFFF');
    setSelectedId(null);
  };

  const addPage = () => {
    // Save current page first
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = {
      ...updatedPages[currentPageIndex],
      elements: [...elements],
      backgroundColor: selectedBgColor,
    };
    const newPage = { id: `page_${Date.now()}`, elements: [], backgroundColor: '#FFFFFF', backgroundImage: null };
    updatedPages.push(newPage);
    setPages(updatedPages);
    // Switch to the new page
    setCurrentPageIndex(updatedPages.length - 1);
    setElements([]);
    setSelectedBgColor('#FFFFFF');
    setSelectedId(null);
    toast.success(`Page ${updatedPages.length} added`);
  };

  const deletePage = (index) => {
    if (pages.length <= 1) {
      toast.error('Cannot delete the only page');
      return;
    }
    const updatedPages = [...pages];
    updatedPages.splice(index, 1);
    setPages(updatedPages);
    const newIndex = Math.min(currentPageIndex, updatedPages.length - 1);
    setCurrentPageIndex(newIndex);
    setElements(updatedPages[newIndex]?.elements || []);
    setSelectedBgColor(updatedPages[newIndex]?.backgroundColor || '#FFFFFF');
    setSelectedId(null);
    toast.success('Page deleted');
  };

  const movePage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= pages.length) return;
    // Save current elements first
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = {
      ...updatedPages[currentPageIndex],
      elements: [...elements],
      backgroundColor: selectedBgColor,
    };
    const [moved] = updatedPages.splice(fromIndex, 1);
    updatedPages.splice(toIndex, 0, moved);
    setPages(updatedPages);
    // Track current page
    if (currentPageIndex === fromIndex) {
      setCurrentPageIndex(toIndex);
    } else if (fromIndex < currentPageIndex && toIndex >= currentPageIndex) {
      setCurrentPageIndex(currentPageIndex - 1);
    } else if (fromIndex > currentPageIndex && toIndex <= currentPageIndex) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  // Initialize pages when type changes
  useEffect(() => {
    if (isMultiPage && pages.length === 1 && pages[0].elements.length === 0) {
      if (type === 'postcard') {
        setPages([
          { id: 'page_front', elements: [], backgroundColor: '#FFFFFF', backgroundImage: null },
          { id: 'page_back', elements: [], backgroundColor: '#FFFFFF', backgroundImage: null },
        ]);
      } else if (type === 'carousel') {
        // Default carousel starts with 5 slides
        const defaultSlides = Array.from({ length: 5 }, (_, i) => ({
          id: `slide_${i + 1}`,
          elements: [],
          backgroundColor: '#FFFFFF',
          backgroundImage: null,
          label: `Slide ${i + 1}`,
        }));
        setPages(defaultSlides);
      }
    }
  }, [type]);

  // Capture page thumbnail from canvas (debounced)
  const captureThumbTimerRef = useRef(null);
  const captureThumbnail = useCallback(() => {
    if (!isMultiPage || !stageRef.current) return;
    try {
      const dataUrl = stageRef.current.toDataURL({ pixelRatio: 0.15 });
      setPageThumbnails(prev => ({ ...prev, [currentPageIndex]: dataUrl }));
    } catch (e) { /* canvas tainted or not ready */ }
  }, [isMultiPage, currentPageIndex]);

  useEffect(() => {
    if (!isMultiPage) return;
    if (captureThumbTimerRef.current) clearTimeout(captureThumbTimerRef.current);
    captureThumbTimerRef.current = setTimeout(captureThumbnail, 800);
    return () => clearTimeout(captureThumbTimerRef.current);
  }, [elements, selectedBgColor, isMultiPage, captureThumbnail]);

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
  }, [templateId]);

  // Load PDF import data from sessionStorage
  useEffect(() => {
    const importType = searchParams.get('import');
    if (importType === 'pdf') {
      try {
        const raw = sessionStorage.getItem('pdf_import_data');
        if (raw) {
          const data = JSON.parse(raw);
          sessionStorage.removeItem('pdf_import_data');

          const API_BASE = process.env.REACT_APP_BACKEND_URL;

          // Multi-page PDF import
          if (data.pages && data.pages.length > 0) {
            const importedPages = data.pages.map((pageData, idx) => {
              const pageElements = [];
              if (pageData.background_image) {
                const bgSrc = pageData.background_image.startsWith('/api')
                  ? API_BASE + pageData.background_image : pageData.background_image;
                pageElements.push({
                  id: `pdf_bg_p${idx}_${Date.now()}`, type: 'image',
                  x: 0, y: 0, width: canvasWidth, height: canvasHeight,
                  src: bgSrc, draggable: false, locked: true, opacity: 1, rotation: 0,
                });
              }
              if (pageData.elements) {
                for (const el of pageData.elements) {
                  const resolved = { ...el, draggable: true };
                  if (resolved.src && resolved.src.startsWith('/api')) {
                    resolved.src = API_BASE + resolved.src;
                  }
                  pageElements.push(resolved);
                }
              }
              return {
                id: `page_${idx + 1}`,
                elements: pageElements,
                backgroundColor: '#FFFFFF',
                backgroundImage: null,
              };
            });
            setPages(importedPages);
            setCurrentPageIndex(0);
            setElements(importedPages[0].elements);
            setTemplateName('Imported PDF Template');
            toast.success(`PDF imported: ${importedPages.length} pages, ${data.pages.reduce((a, p) => a + (p.elements?.length || 0), 0)} elements`);
          }
          // Single page PDF import (backward compat)
          else {
            const newElements = [];
            if (data.background_image) {
              const bgSrc = data.background_image.startsWith('/api')
                ? API_BASE + data.background_image : data.background_image;
              newElements.push({
                id: 'pdf_bg_' + Date.now(), type: 'image',
                x: 0, y: 0, width: canvasWidth, height: canvasHeight,
                src: bgSrc, draggable: false, locked: true, opacity: 1, rotation: 0,
              });
            }
            if (data.elements) {
              for (const el of data.elements) {
                const resolved = { ...el, draggable: true };
                if (resolved.src && resolved.src.startsWith('/api')) {
                  resolved.src = API_BASE + resolved.src;
                }
                newElements.push(resolved);
              }
            }
            setElements(newElements);
            setTemplateName('Imported PDF Template');
            if (data.background_color) setSelectedBgColor(data.background_color);
            toast.success(`PDF imported with ${data.elements?.length || 0} elements`);
          }
        }
      } catch (err) {
        console.error('Failed to load PDF import data:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      const element = elements.find(el => el.id === selectedId);
      setSelectedElement(element);
      if (element) {
        setSelectedColor(element.fill || element.backgroundColor || '#064E3B');
        if (element.type === 'text') {
          setFontSize(element.fontSize || 48);
          setFontFamily(element.fontFamily || 'Playfair Display');
          setTextAlign(element.align || 'left');
          setFontWeight(element.fontStyle?.includes('bold') ? 'bold' : 'normal');
          setFontStyle(element.fontStyle?.includes('italic') ? 'italic' : 'normal');
        }
        setOpacity(element.opacity || 1);
      }
    } else {
      setSelectedElement(null);
    }
  }, [selectedId, elements]);

  const loadTemplate = async () => {
    try {
      const response = await apiClient.get(`/templates/${templateId}`);
      const template = response.data;
      setTemplateName(template.name);
      setCategory(template.category);
      setType(template.type);
      setThumbnailUrl(template.thumbnail_url || '');
      
      if (template.canvas_data) {
        const cd = template.canvas_data;
        
        // Multi-page template
        if (cd.isMultiPage && cd.pages) {
          setPages(cd.pages.map(p => ({
            id: p.id || `page_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
            elements: p.elements || [],
            backgroundColor: p.backgroundColor || '#FFFFFF',
            backgroundImage: p.backgroundImage || null,
          })));
          setCurrentPageIndex(0);
          setElements(cd.pages[0]?.elements || []);
          setSelectedBgColor(cd.pages[0]?.backgroundColor || '#FFFFFF');
          if (cd.postcardSize) {
            setPostcardSize(cd.postcardSize);
            // For custom sizes, restore the inch inputs from the saved canvas dimensions
            if (cd.postcardSize === 'custom' && cd.width && cd.height) {
              setPostcardCustomW(Math.round((cd.width / POSTCARD_DPI) * 10) / 10);
              setPostcardCustomH(Math.round((cd.height / POSTCARD_DPI) * 10) / 10);
            }
          }
        }
        // Legacy multi-sided (backward compat)
        else if (cd.isMultiSided && cd.sides) {
          const loadedPages = [
            { id: 'page_front', elements: cd.sides.front?.elements || [], backgroundColor: cd.sides.front?.backgroundColor || '#FFFFFF', backgroundImage: null },
            { id: 'page_back', elements: cd.sides.back?.elements || [], backgroundColor: cd.sides.back?.backgroundColor || '#FFFFFF', backgroundImage: null },
          ];
          setPages(loadedPages);
          setCurrentPageIndex(0);
          setElements(loadedPages[0].elements);
          setSelectedBgColor(loadedPages[0].backgroundColor);
        }
        // Single page
        else {
          setElements(cd.elements || []);
          setSelectedBgColor(cd.backgroundColor || '#FFFFFF');
        }
      }
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const addToHistory = useCallback(() => {
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push(JSON.parse(JSON.stringify(elements)));
    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  }, [history, historyStep, elements]);

  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
      setElements(history[historyStep - 1]);
    }
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
      setElements(history[historyStep + 1]);
    }
  };

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl/Cmd+Y (redo),
  // Delete/Backspace (remove selected element). Skips while typing in inputs.
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (historyStep > 0) {
          setHistoryStep(historyStep - 1);
          setElements(history[historyStep - 1]);
        }
        return;
      }
      if ((isMod && e.shiftKey && e.key.toLowerCase() === 'z') || (isMod && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        if (historyStep < history.length - 1) {
          setHistoryStep(historyStep + 1);
          setElements(history[historyStep + 1]);
        }
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        e.preventDefault();
        setElements((prev) => prev.filter((el) => el.id !== selectedId));
        setSelectedId(null);
        addToHistory();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [history, historyStep, selectedId, addToHistory]);

  const addTextElement = () => {
    const newElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: 'Double click to edit',
      x: 100,
      y: 100,
      width: 300,
      fontSize: fontSize,
      fill: selectedColor,
      fontFamily: fontFamily,
      align: textAlign,
      fontStyle: `${fontWeight} ${fontStyle}`,
      draggable: true,
      visible: true,
      locked: false,
      opacity: opacity
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
  };

  const addRectElement = () => {
    const newElement = {
      id: `rect-${Date.now()}`,
      type: 'rect',
      x: 150,
      y: 150,
      width: 300,
      height: 200,
      fill: selectedColor,
      draggable: true,
      visible: true,
      locked: false,
      opacity: opacity
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
  };

  const addCircleElement = () => {
    const newElement = {
      id: `circle-${Date.now()}`,
      type: 'circle',
      x: 200,
      y: 200,
      radius: 100,
      fill: selectedColor,
      draggable: true,
      visible: true,
      locked: false,
      opacity: opacity
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
  };

  const addLineElement = () => {
    const newElement = {
      id: `line-${Date.now()}`,
      type: 'line',
      points: [100, 100, 400, 100],
      stroke: selectedColor,
      strokeWidth: 5,
      draggable: true,
      visible: true,
      locked: false,
      opacity: opacity
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
  };

  const addLogoPlaceholder = () => {
    const newElement = {
      id: `logo-placeholder-${Date.now()}`,
      type: 'logoPlaceholder',
      x: 100,
      y: 100,
      width: 200,
      height: 80,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      label: 'LOGO',
      backgroundColor: '#E5E5E5',
      borderColor: '#999999'
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success('Logo placeholder added - will auto-fill from user brand kit');
  };

  const addHeadshotPlaceholder = () => {
    const newElement = {
      id: `headshot-placeholder-${Date.now()}`,
      type: 'headshotPlaceholder',
      x: 100,
      y: 100,
      width: 180,
      height: 180,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      label: 'HEADSHOT',
      backgroundColor: '#F5F5F5',
      borderColor: '#999999',
      cornerRadius: 90, // circular by default
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success('Agent headshot placeholder added — auto-fills from Brand Kit');
  };

  const addPhotoPlaceholder = () => {
    const newElement = {
      id: `photo-placeholder-${Date.now()}`,
      type: 'photoPlaceholder',
      x: 100,
      y: 150,
      width: 400,
      height: 300,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      label: 'PROPERTY PHOTO',
      backgroundColor: '#F0F0F0',
      borderColor: '#CCCCCC'
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success('Photo placeholder added - users will upload their property image here');
  };

  const addQRCodePlaceholder = () => {
    const newElement = {
      id: `qr-placeholder-${Date.now()}`,
      type: 'qrCodePlaceholder',
      x: 100,
      y: 150,
      width: 150,
      height: 150,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      label: 'QR CODE',
      backgroundColor: '#FFFFFF',
      borderColor: '#000000',
      qrUrl: '' // Will be populated from listing URL
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success('QR Code placeholder added - will auto-generate from listing URL');
  };

  // Photo Grid for admin canvas designer
  const PHOTO_GRID_CONFIGS = [
    { id: '2-horizontal', name: '2 Photos (H)', slots: [{x:0,y:0,w:0.5,h:1},{x:0.5,y:0,w:0.5,h:1}] },
    { id: '2-vertical', name: '2 Photos (V)', slots: [{x:0,y:0,w:1,h:0.5},{x:0,y:0.5,w:1,h:0.5}] },
    { id: '3-row', name: '3 Photos (Row)', slots: [{x:0,y:0,w:0.333,h:1},{x:0.333,y:0,w:0.334,h:1},{x:0.667,y:0,w:0.333,h:1}] },
    { id: '3-feature', name: '3 Feature', slots: [{x:0,y:0,w:0.6,h:1},{x:0.6,y:0,w:0.4,h:0.5},{x:0.6,y:0.5,w:0.4,h:0.5}] },
    { id: '4-grid', name: '4 Grid', slots: [{x:0,y:0,w:0.5,h:0.5},{x:0.5,y:0,w:0.5,h:0.5},{x:0,y:0.5,w:0.5,h:0.5},{x:0.5,y:0.5,w:0.5,h:0.5}] },
  ];
  const [showAdminGridPicker, setShowAdminGridPicker] = useState(false);

  const addPhotoGrid = (gridConfig) => {
    const gridWidth = 400, gridHeight = 300, gap = 4;
    const slots = gridConfig.slots.map(s => ({
      x: 100 + s.x * gridWidth + (s.x > 0 ? gap : 0),
      y: 100 + s.y * gridHeight + (s.y > 0 ? gap : 0),
      width: s.w * gridWidth - (s.w < 1 ? gap : 0),
      height: s.h * gridHeight - (s.h < 1 ? gap : 0),
      src: null
    }));
    const newElement = {
      id: `photo-grid-${Date.now()}`,
      type: 'photoGrid',
      gridType: gridConfig.id,
      x: 100, y: 100,
      width: gridWidth, height: gridHeight,
      slots,
      draggable: true, visible: true, locked: false, opacity: 1,
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    setShowAdminGridPicker(false);
    toast.success(`${gridConfig.name} photo grid added`);
  };

  // Property field placeholders for RealProp Magic Import compatibility
  const addPropertyPlaceholder = (fieldType) => {
    const fieldConfigs = {
      price: {
        id: 'price',
        text: '${{price}}',
        fontSize: 48,
        fontStyle: 'bold',
        fill: '#FFFFFF',
        label: 'Price'
      },
      address: {
        id: 'address',
        text: '{{address}}',
        fontSize: 24,
        fill: '#CCCCCC',
        label: 'Address'
      },
      beds: {
        id: 'beds-text',
        text: '{{beds}} Beds',
        fontSize: 20,
        fill: '#FFFFFF',
        label: 'Bedrooms'
      },
      baths: {
        id: 'baths-text',
        text: '{{baths}} Baths',
        fontSize: 20,
        fill: '#FFFFFF',
        label: 'Bathrooms'
      },
      sqft: {
        id: 'sqft-text',
        text: '{{sqft}} Sq Ft',
        fontSize: 20,
        fill: '#FFFFFF',
        label: 'Square Feet'
      },
      details: {
        id: 'details',
        text: '{{beds}} Beds  •  {{baths}} Baths  •  {{sqft}} Sq Ft',
        fontSize: 20,
        fill: '#AAAAAA',
        label: 'Property Details'
      },
      key_features: {
        id: 'key-features',
        text: '{{key_features}}',
        fontSize: 16,
        fill: '#666666',
        width: 400,
        lineHeight: 1.8,
        label: 'Key Features'
      },
      summary: {
        id: 'description',
        text: '{{summary}}',
        fontSize: 18,
        fill: '#666666',
        width: 500,
        lineHeight: 1.5,
        label: 'Property Description'
      }
    };

    const config = fieldConfigs[fieldType];
    if (!config) return;

    const newElement = {
      id: `${config.id}-${Date.now()}`,
      type: 'text',
      x: 100,
      y: 200 + elements.length * 30,
      text: config.text,
      fontSize: config.fontSize,
      fontFamily: 'Montserrat',
      fill: config.fill,
      fontStyle: config.fontStyle || 'normal',
      width: config.width,
      lineHeight: config.lineHeight,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      isPropertyField: true
    };

    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success(`${config.label} placeholder added - will auto-fill from RealProp Magic Import`);
  };

  // Agent identity placeholders — auto-fill from the user's Brand Kit / profile
  // when they create a campaign from this template. Tokens match the
  // hydration map in /app/backend/routes/scraper.py _apply_property_to_canvas.
  const addAgentPlaceholder = (fieldType) => {
    const agentConfigs = {
      agent_name: { id: 'agent-name', text: '{{agent_name}}', fontSize: 28, fontStyle: 'bold', fill: '#1F2937', label: 'Agent Name' },
      agent_title: { id: 'agent-title', text: '{{agent_title}}', fontSize: 18, fill: '#6B7280', label: 'Agent Title' },
      agent_phone: { id: 'agent-phone', text: '{{agent_phone}}', fontSize: 18, fill: '#1F2937', label: 'Agent Phone' },
      agent_email: { id: 'agent-email', text: '{{agent_email}}', fontSize: 18, fill: '#1F2937', label: 'Agent Email' },
      agent_website: { id: 'agent-website', text: '{{agent_website}}', fontSize: 16, fill: '#1F2937', label: 'Agent Website' },
      agent_license: { id: 'agent-license', text: 'Lic. {{agent_license}}', fontSize: 14, fill: '#6B7280', label: 'License #' },
      brokerage: { id: 'brokerage', text: '{{brokerage}}', fontSize: 18, fontStyle: 'bold', fill: '#1F2937', label: 'Brokerage' },
      brokerage_address: { id: 'brokerage-address', text: '{{brokerage_address}}', fontSize: 14, fill: '#6B7280', width: 280, lineHeight: 1.4, label: 'Brokerage Address' },
      signature_block: {
        id: 'agent-signature',
        text: '{{agent_name}}\n{{agent_title}} · {{brokerage}}\n{{agent_phone}} · {{agent_email}}',
        fontSize: 16,
        fill: '#1F2937',
        width: 360,
        lineHeight: 1.5,
        label: 'Agent Signature Block',
      },
    };
    const config = agentConfigs[fieldType];
    if (!config) return;
    const newElement = {
      id: `${config.id}-${Date.now()}`,
      type: 'text',
      x: 100,
      y: 200 + elements.length * 30,
      text: config.text,
      fontSize: config.fontSize,
      fontFamily: 'Montserrat',
      fill: config.fill,
      fontStyle: config.fontStyle || 'normal',
      width: config.width,
      lineHeight: config.lineHeight,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1,
      isAgentField: true,
    };
    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success(`${config.label} placeholder added — auto-fills from agent's Brand Kit`);
  };

  // Newsletter-specific block elements
  const addNewsletterBlock = (blockType) => {
    const baseBlock = {
      id: `newsletter-${blockType}-${Date.now()}`,
      type: 'newsletterBlock',
      blockType,
      x: 0,
      y: elements.filter(el => el.type === 'newsletterBlock').length * 100,
      width: canvasWidth,
      draggable: true,
      visible: true,
      locked: false,
      opacity: 1
    };

    let newElement;
    switch (blockType) {
      case 'header':
        newElement = { ...baseBlock, height: 100, backgroundColor: '#064E3B', textColor: '#FFFFFF', title: 'Newsletter Header' };
        break;
      case 'logoBlock':
        newElement = { ...baseBlock, height: 80, backgroundColor: '#FFFFFF', logoAlignment: 'center' };
        break;
      case 'textBlock':
        newElement = { ...baseBlock, height: 120, backgroundColor: '#FFFFFF', textColor: '#333333', content: 'Your content here...' };
        break;
      case 'imageBlock':
        newElement = { ...baseBlock, height: 200, imageUrl: '' };
        break;
      case 'buttonBlock':
        newElement = { ...baseBlock, height: 80, buttonColor: '#064E3B', buttonText: 'Click Here', buttonUrl: 'https://' };
        break;
      case 'divider':
        newElement = { ...baseBlock, height: 40, dividerColor: '#E5E5E5' };
        break;
      case 'spacer':
        newElement = { ...baseBlock, height: 30 };
        break;
      case 'signatureBlock':
        newElement = { 
          ...baseBlock, 
          height: 120, 
          backgroundColor: '#F9FAFB', 
          signatureName: 'Your Name',
          signatureTitle: 'Real Estate Agent',
          signaturePhone: '(555) 123-4567',
          signatureEmail: 'agent@example.com'
        };
        break;
      default:
        return;
    }

    setElements([...elements, newElement]);
    setSelectedId(newElement.id);
    addToHistory();
    toast.success(`${blockType} block added`);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newElement = {
          id: `image-${Date.now()}`,
          type: 'image',
          src: event.target.result,
          x: 100,
          y: 100,
          width: 400,
          height: 300,
          draggable: true,
          visible: true,
          locked: false,
          opacity: opacity
        };
        setElements([...elements, newElement]);
        setSelectedId(newElement.id);
        addToHistory();
      };
      reader.readAsDataURL(file);
    }
  };

  const duplicateElement = () => {
    if (selectedElement) {
      const newElement = {
        ...selectedElement,
        id: `${selectedElement.type}-${Date.now()}`,
        x: selectedElement.x + 20,
        y: selectedElement.y + 20
      };
      setElements([...elements, newElement]);
      setSelectedId(newElement.id);
      addToHistory();
    }
  };

  const deleteSelected = () => {
    if (selectedId) {
      setElements(elements.filter(el => el.id !== selectedId));
      setSelectedId(null);
      addToHistory();
    }
  };

  const toggleVisibility = (id) => {
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, visible: !el.visible };
      }
      return el;
    });
    setElements(updatedElements);
  };

  const toggleLock = (id) => {
    const updatedElements = elements.map(el => {
      if (el.id === id) {
        return { ...el, locked: !el.locked, draggable: el.locked };
      }
      return el;
    });
    setElements(updatedElements);
  };

  const updateElement = useCallback((id, updates) => {
    setElements(prev => prev.map(el => el.id === id ? { ...el, ...updates } : el));
  }, []);

  const updateSelectedElement = (updates) => {
    if (selectedId) {
      updateElement(selectedId, updates);
    }
  };

  const handleElementClick = (id) => {
    setSelectedId(id);
  };

  const moveLayer = (id, direction) => {
    const index = elements.findIndex(el => el.id === id);
    if (direction === 'up' && index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      setElements(newElements);
    } else if (direction === 'down' && index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      setElements(newElements);
    }
  };

  const exportAsImage = () => {
    setSelectedId(null);
    setTimeout(() => {
      const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `${templateName}.png`;
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Template exported as image');
    }, 100);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      let canvasData;
      
      if (isMultiPage) {
        // Save current page first
        const updatedPages = [...pages];
        updatedPages[currentPageIndex] = {
          ...updatedPages[currentPageIndex],
          elements: [...elements],
          backgroundColor: selectedBgColor,
        };
        
        // Multi-page template structure
        canvasData = {
          width: canvasWidth,
          height: canvasHeight,
          isMultiPage: true,
          pageCount: updatedPages.length,
          pages: updatedPages.map((p, i) => ({
            id: p.id,
            elements: p.elements,
            backgroundColor: p.backgroundColor || '#FFFFFF',
            backgroundImage: p.backgroundImage || null,
            label: type === 'postcard' && i < 2 ? (i === 0 ? 'Front' : 'Back') : type === 'carousel' ? `Slide ${i + 1}` : `Page ${i + 1}`,
          })),
          postcardSize: type === 'postcard' ? postcardSize : undefined,
        };
      } else {
        // Single-page template
        canvasData = {
          width: canvasWidth,
          height: canvasHeight,
          backgroundColor: selectedBgColor,
          elements: elements
        };
      }
      
      const templateData = {
        name: templateName,
        category,
        type,
        thumbnail_url: thumbnailUrl || null,
        canvas_data: canvasData
      };
      
      if (templateId) {
        await apiClient.put(`/templates/${templateId}`, templateData);
        toast.success('Template updated successfully');
      } else {
        await apiClient.post('/templates', templateData);
        toast.success('Template created successfully');
      }
      
      navigate(returnTo);
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const checkDeselect = (e) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
    }
  };

  // Render newsletter block properties
  const renderNewsletterBlockProperties = () => {
    if (!selectedElement || selectedElement.type !== 'newsletterBlock') return null;

    return (
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Block Height</Label>
          <Slider
            value={[selectedElement.height]}
            onValueChange={([val]) => updateSelectedElement({ height: val })}
            min={30}
            max={400}
            step={10}
          />
        </div>

        {selectedElement.blockType === 'header' && (
          <>
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={selectedElement.title || ''}
                onChange={(e) => updateSelectedElement({ title: e.target.value })}
                className="h-9"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Background</Label>
                <Input
                  type="color"
                  value={selectedElement.backgroundColor || '#064E3B'}
                  onChange={(e) => updateSelectedElement({ backgroundColor: e.target.value })}
                  className="h-9 w-full"
                />
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <Input
                  type="color"
                  value={selectedElement.textColor || '#FFFFFF'}
                  onChange={(e) => updateSelectedElement({ textColor: e.target.value })}
                  className="h-9 w-full"
                />
              </div>
            </div>
          </>
        )}

        {selectedElement.blockType === 'textBlock' && (
          <>
            <div>
              <Label className="text-xs">Content Preview</Label>
              <Textarea
                value={selectedElement.content || ''}
                onChange={(e) => updateSelectedElement({ content: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Background</Label>
                <Input
                  type="color"
                  value={selectedElement.backgroundColor || '#FFFFFF'}
                  onChange={(e) => updateSelectedElement({ backgroundColor: e.target.value })}
                  className="h-9 w-full"
                />
              </div>
              <div>
                <Label className="text-xs">Text Color</Label>
                <Input
                  type="color"
                  value={selectedElement.textColor || '#333333'}
                  onChange={(e) => updateSelectedElement({ textColor: e.target.value })}
                  className="h-9 w-full"
                />
              </div>
            </div>
          </>
        )}

        {selectedElement.blockType === 'buttonBlock' && (
          <>
            <div>
              <Label className="text-xs">Button Text</Label>
              <Input
                value={selectedElement.buttonText || ''}
                onChange={(e) => updateSelectedElement({ buttonText: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Button URL</Label>
              <Input
                value={selectedElement.buttonUrl || ''}
                onChange={(e) => updateSelectedElement({ buttonUrl: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Button Color</Label>
              <Input
                type="color"
                value={selectedElement.buttonColor || '#064E3B'}
                onChange={(e) => updateSelectedElement({ buttonColor: e.target.value })}
                className="h-9 w-full"
              />
            </div>
          </>
        )}

        {selectedElement.blockType === 'divider' && (
          <div>
            <Label className="text-xs">Divider Color</Label>
            <Input
              type="color"
              value={selectedElement.dividerColor || '#E5E5E5'}
              onChange={(e) => updateSelectedElement({ dividerColor: e.target.value })}
              className="h-9 w-full"
            />
          </div>
        )}

        {selectedElement.blockType === 'signatureBlock' && (
          <>
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={selectedElement.signatureName || ''}
                onChange={(e) => updateSelectedElement({ signatureName: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={selectedElement.signatureTitle || ''}
                onChange={(e) => updateSelectedElement({ signatureTitle: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={selectedElement.signaturePhone || ''}
                onChange={(e) => updateSelectedElement({ signaturePhone: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                value={selectedElement.signatureEmail || ''}
                onChange={(e) => updateSelectedElement({ signatureEmail: e.target.value })}
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Background</Label>
              <Input
                type="color"
                value={selectedElement.backgroundColor || '#F9FAFB'}
                onChange={(e) => updateSelectedElement({ backgroundColor: e.target.value })}
                className="h-9 w-full"
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Canvas Designer</h1>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={undo} disabled={historyStep === 0} title="Undo (Ctrl/Cmd+Z)" data-testid="canvas-designer-undo-btn">Undo</Button>
            <Button variant="outline" onClick={redo} disabled={historyStep === history.length - 1} title="Redo (Ctrl/Cmd+Shift+Z)" data-testid="canvas-designer-redo-btn">Redo</Button>
            <UserMenu />
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Sidebar - Tools & Properties */}
        <div className="w-80 border-r border-border/50 bg-card overflow-y-auto">
          {/* Template Details */}
          <div className="p-6 border-b border-border/50">
            <h3 className="font-semibold mb-4">Template Details</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="My Template"
                  className="h-9"
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-9" data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_listing">New Listings</SelectItem>
                    <SelectItem value="open_house">Open House</SelectItem>
                    <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                    <SelectItem value="under_contract">Under Contract</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                    <SelectItem value="market_report">Market Report</SelectItem>
                    <SelectItem value="festive">Festive Post</SelectItem>
                    <SelectItem value="misc">Misc</SelectItem>
                    {(type === 'flyer' || type === 'postcard' || type === 'brochure') && (
                      <>
                        <SelectItem value="brochure">Brochure</SelectItem>
                        <SelectItem value="flyer_cat">Flyer</SelectItem>
                        <SelectItem value="postcards">Postcards</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="h-9" data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">Post (1080x1080)</SelectItem>
                    <SelectItem value="story">Story (1080x1920)</SelectItem>
                    <SelectItem value="reel">Reel (1080x1920)</SelectItem>
                    <SelectItem value="flyer">Flyer (8.5x11")</SelectItem>
                    <SelectItem value="postcard">Postcard - 2 Sides</SelectItem>
                    <SelectItem value="brochure">Brochure (11x8.5")</SelectItem>
                    <SelectItem value="carousel">Carousel (1080x1080)</SelectItem>
                    <SelectItem value="newsletter">Newsletter (600x800)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Add Elements - Conditional based on type */}
          <div className="p-6 border-b border-border/50">
            <h3 className="font-semibold mb-4">Add Elements</h3>
            
            {type !== 'newsletter' ? (
              <>
                {/* Standard elements for post/story/reel */}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={addTextElement} data-testid="add-text-btn">
                    <Type className="h-4 w-4 mr-1" />
                    Text
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="add-image-btn">
                    <Image className="h-4 w-4 mr-1" />
                    Image
                  </Button>
                  <Button variant="outline" size="sm" onClick={addRectElement} data-testid="add-rect-btn">
                    <Square className="h-4 w-4 mr-1" />
                    Rectangle
                  </Button>
                  <Button variant="outline" size="sm" onClick={addCircleElement} data-testid="add-circle-btn">
                    <CircleIcon className="h-4 w-4 mr-1" />
                    Circle
                  </Button>
                  <Button variant="outline" size="sm" onClick={addLineElement} data-testid="add-line-btn">
                    <Minus className="h-4 w-4 mr-1" />
                    Line
                  </Button>
                </div>
                
                <h4 className="font-medium text-sm mt-4 mb-2 text-muted-foreground">Placeholders</h4>
                <div className="grid grid-cols-3 gap-2">
                  <Button variant="outline" size="sm" onClick={addLogoPlaceholder} className="border-dashed" data-testid="add-logo-placeholder-btn">
                    <Image className="h-4 w-4 mr-1" />
                    Logo
                  </Button>
                  <Button variant="outline" size="sm" onClick={addHeadshotPlaceholder} className="border-dashed" data-testid="add-headshot-placeholder-btn">
                    <UserCircle className="h-4 w-4 mr-1" />
                    Headshot
                  </Button>
                  <Button variant="outline" size="sm" onClick={addPhotoPlaceholder} className="border-dashed" data-testid="add-photo-placeholder-btn">
                    <Image className="h-4 w-4 mr-1" />
                    Photo
                  </Button>
                  <Button variant="outline" size="sm" onClick={addQRCodePlaceholder} className="border-dashed" data-testid="add-qr-placeholder-btn">
                    <Grid3X3 className="h-4 w-4 mr-1" />
                    QR Code
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowAdminGridPicker(!showAdminGridPicker)} className="border-dashed" data-testid="add-photo-grid-btn">
                    <Grid3X3 className="h-4 w-4 mr-1" />
                    Photo Grid
                  </Button>
                </div>
                {showAdminGridPicker && (
                  <div className="mt-2 grid grid-cols-2 gap-1.5 p-2 bg-muted/40 rounded-md border">
                    {PHOTO_GRID_CONFIGS.map(gc => (
                      <Button key={gc.id} variant="ghost" size="sm" className="text-xs justify-start h-7" onClick={() => addPhotoGrid(gc)} data-testid={`grid-option-${gc.id}`}>
                        {gc.name}
                      </Button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Logo auto-fills from user's brand kit. Photo is for property images. QR code links to listing URL.
                </p>
                
                {/* Property Field Placeholders for RealProp Magic Import */}
                <h4 className="font-medium text-sm mt-4 mb-2 text-muted-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  RealProp Magic Import Fields
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('price')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-price-placeholder">
                    <DollarSign className="h-4 w-4 mr-1" />
                    Price
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('address')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-address-placeholder">
                    <MapPin className="h-4 w-4 mr-1" />
                    Address
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('beds')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-beds-placeholder">
                    <Bed className="h-4 w-4 mr-1" />
                    Beds
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('baths')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-baths-placeholder">
                    <Bath className="h-4 w-4 mr-1" />
                    Baths
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('sqft')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-sqft-placeholder">
                    <Ruler className="h-4 w-4 mr-1" />
                    Sq Ft
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('details')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-details-placeholder">
                    <LayoutList className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('key_features')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-features-placeholder">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Key Features (5 bullets)
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addPropertyPlaceholder('summary')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-summary-placeholder">
                    <FileText className="h-4 w-4 mr-1" />
                    Property Description
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  These fields auto-fill when users create designs via RealProp Magic Import. Use these to make templates RealProp Magic Import compatible.
                </p>

                {/* Agent Info Placeholders — auto-fill from end-user's Brand Kit */}
                <h4 className="font-medium text-sm mt-4 mb-2 text-muted-foreground flex items-center gap-2">
                  <UserCircle className="h-4 w-4 text-primary" />
                  Agent Info Fields
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_name')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-name-placeholder">
                    <UserCircle className="h-4 w-4 mr-1" />
                    Name
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_title')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-title-placeholder">
                    <FileText className="h-4 w-4 mr-1" />
                    Title
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_phone')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-phone-placeholder">
                    <Type className="h-4 w-4 mr-1" />
                    Phone
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_email')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-email-placeholder">
                    <Link2 className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_website')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-website-placeholder">
                    <Link2 className="h-4 w-4 mr-1" />
                    Website
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('agent_license')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-license-placeholder">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    License #
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('brokerage')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-brokerage-placeholder">
                    <Layout className="h-4 w-4 mr-1" />
                    Brokerage
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('brokerage_address')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-brokerage-address-placeholder">
                    <MapPin className="h-4 w-4 mr-1" />
                    Address
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => addAgentPlaceholder('signature_block')} className="border-primary/50 text-primary hover:bg-primary/10" data-testid="add-agent-signature-placeholder">
                    <LayoutList className="h-4 w-4 mr-1" />
                    Signature Block (name · title · contact)
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Agent fields auto-fill from each user's Brand Kit when they use this template. Pair with the Headshot / Logo placeholders above for a complete signature.
                </p>
              </>
            ) : (
              <>
                {/* Newsletter-specific blocks */}
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('header')} data-testid="add-newsletter-header-btn">
                    <Layout className="h-4 w-4 mr-2" />
                    Header Block
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('logoBlock')} data-testid="add-newsletter-logo-btn">
                    <Image className="h-4 w-4 mr-2" />
                    Logo Block
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('textBlock')} data-testid="add-newsletter-text-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Text Block
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('imageBlock')} data-testid="add-newsletter-image-btn">
                    <Image className="h-4 w-4 mr-2" />
                    Image Block
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('buttonBlock')} data-testid="add-newsletter-button-btn">
                    <Link2 className="h-4 w-4 mr-2" />
                    Button Block
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('divider')} data-testid="add-newsletter-divider-btn">
                    <Minus className="h-4 w-4 mr-2" />
                    Divider
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('spacer')} data-testid="add-newsletter-spacer-btn">
                    <Square className="h-4 w-4 mr-2" />
                    Spacer
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => addNewsletterBlock('signatureBlock')} data-testid="add-newsletter-signature-btn">
                    <FileText className="h-4 w-4 mr-2" />
                    Signature Block
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Newsletter templates use content blocks. Logo block auto-fills from user's brand kit. Users will customize text and images when creating campaigns.
                </p>
              </>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
          </div>

          {/* Element Properties */}
          {selectedElement && (
            <div className="p-6 border-b border-border/50">
              <h3 className="font-semibold mb-4">Properties</h3>
              <div className="space-y-3">
                {/* Newsletter block properties */}
                {selectedElement.type === 'newsletterBlock' && renderNewsletterBlockProperties()}

                {/* Text properties */}
                {selectedElement.type === 'text' && (
                  <>
                    {/* Placeholder Mapping */}
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                      <Label className="text-xs font-semibold text-primary mb-2 block">Placeholder Mapping</Label>
                      {selectedElement.isPropertyField ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 rounded-md bg-primary/10 text-primary font-medium" data-testid="placeholder-badge">
                            {selectedElement.placeholderField || (selectedElement.text?.match(/\{\{(\w+)\}\}/)?.[1]) || 'mapped'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-muted-foreground"
                            onClick={() => updateSelectedElement({ isPropertyField: false, placeholderField: null })}
                            data-testid="remove-placeholder-btn"
                          >
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <Select
                          value=""
                          onValueChange={(val) => {
                            const fieldLabels = {
                              price: '${{price}}', address: '{{address}}', beds: '{{beds}} Beds',
                              baths: '{{baths}} Baths', sqft: '{{sqft}} Sq Ft',
                              details: '{{beds}} Beds  \u2022  {{baths}} Baths  \u2022  {{sqft}} Sq Ft',
                              key_features: '{{key_features}}', summary: '{{summary}}',
                              agent_name: '{{agent_name}}', agent_phone: '{{agent_phone}}',
                              agent_email: '{{agent_email}}', agent_title: '{{agent_title}}',
                              agent_license: 'Lic# {{agent_license}}', agent_website: '{{agent_website}}',
                              brokerage: '{{brokerage}}', property_type: '{{property_type}}'
                            };
                            updateSelectedElement({
                              isPropertyField: true,
                              placeholderField: val,
                              text: fieldLabels[val] || `{{${val}}}`,
                            });
                            toast.success(`Marked as {{${val}}} placeholder`);
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid="placeholder-select">
                            <SelectValue placeholder="Mark as placeholder..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="price">Price</SelectItem>
                            <SelectItem value="address">Address</SelectItem>
                            <SelectItem value="beds">Bedrooms</SelectItem>
                            <SelectItem value="baths">Bathrooms</SelectItem>
                            <SelectItem value="sqft">Square Feet</SelectItem>
                            <SelectItem value="details">Property Details (combined)</SelectItem>
                            <SelectItem value="key_features">Key Features</SelectItem>
                            <SelectItem value="summary">Description</SelectItem>
                            <SelectItem value="property_type">Property Type</SelectItem>
                            <SelectItem value="agent_name">Agent Name</SelectItem>
                            <SelectItem value="agent_phone">Agent Phone</SelectItem>
                            <SelectItem value="agent_email">Agent Email</SelectItem>
                            <SelectItem value="agent_title">Agent Title (Realtor, Broker…)</SelectItem>
                            <SelectItem value="agent_license">Agent License #</SelectItem>
                            <SelectItem value="agent_website">Agent Website</SelectItem>
                            <SelectItem value="brokerage">Brokerage</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">Auto-fills with property data from Magic Import</p>
                    </div>

                    <div>
                      <Label className="text-xs">Text Content</Label>
                      <Textarea
                        value={selectedElement.text}
                        onChange={(e) => updateSelectedElement({ text: e.target.value })}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs">Font Size: {selectedElement.fontSize || fontSize}px</Label>
                      <Slider
                        value={[selectedElement.fontSize || fontSize]}
                        onValueChange={([val]) => {
                          setFontSize(val);
                          updateSelectedElement({ fontSize: val });
                        }}
                        min={12}
                        max={200}
                        step={1}
                      />
                    </div>

                    <div>
                      <Label className="text-xs">Font Family</Label>
                      <Select value={selectedElement.fontFamily || fontFamily} onValueChange={(val) => {
                        setFontFamily(val);
                        updateSelectedElement({ fontFamily: val });
                      }}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                          <SelectItem value="Montserrat">Montserrat</SelectItem>
                          <SelectItem value="Roboto">Roboto</SelectItem>
                          <SelectItem value="Open Sans">Open Sans</SelectItem>
                          <SelectItem value="Lato">Lato</SelectItem>
                          <SelectItem value="Poppins">Poppins</SelectItem>
                          <SelectItem value="Raleway">Raleway</SelectItem>
                          <SelectItem value="Oswald">Oswald</SelectItem>
                          <SelectItem value="Merriweather">Merriweather</SelectItem>
                          <SelectItem value="PT Sans">PT Sans</SelectItem>
                          <SelectItem value="Source Sans Pro">Source Sans Pro</SelectItem>
                          <SelectItem value="Ubuntu">Ubuntu</SelectItem>
                          <SelectItem value="Nunito">Nunito</SelectItem>
                          <SelectItem value="Work Sans">Work Sans</SelectItem>
                          <SelectItem value="Quicksand">Quicksand</SelectItem>
                          <SelectItem value="Bebas Neue">Bebas Neue</SelectItem>
                          <SelectItem value="Dancing Script">Dancing Script</SelectItem>
                          <SelectItem value="Pacifico">Pacifico</SelectItem>
                          <SelectItem value="Arial">Arial</SelectItem>
                          <SelectItem value="Georgia">Georgia</SelectItem>
                          <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs mb-2 block">Text Style</Label>
                      <div className="flex gap-2">
                        <Button 
                          variant={fontWeight === 'bold' ? 'default' : 'outline'} 
                          size="sm"
                          onClick={() => {
                            const newWeight = fontWeight === 'bold' ? 'normal' : 'bold';
                            setFontWeight(newWeight);
                            updateSelectedElement({ fontStyle: `${newWeight} ${fontStyle}` });
                          }}
                        >
                          <Bold className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant={fontStyle === 'italic' ? 'default' : 'outline'} 
                          size="sm"
                          onClick={() => {
                            const newStyle = fontStyle === 'italic' ? 'normal' : 'italic';
                            setFontStyle(newStyle);
                            updateSelectedElement({ fontStyle: `${fontWeight} ${newStyle}` });
                          }}
                        >
                          <Italic className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs mb-2 block">Text Align</Label>
                      <div className="flex gap-2">
                        <Button 
                          variant={textAlign === 'left' ? 'default' : 'outline'} 
                          size="sm"
                          onClick={() => {
                            setTextAlign('left');
                            updateSelectedElement({ align: 'left' });
                          }}
                        >
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant={textAlign === 'center' ? 'default' : 'outline'} 
                          size="sm"
                          onClick={() => {
                            setTextAlign('center');
                            updateSelectedElement({ align: 'center' });
                          }}
                        >
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant={textAlign === 'right' ? 'default' : 'outline'} 
                          size="sm"
                          onClick={() => {
                            setTextAlign('right');
                            updateSelectedElement({ align: 'right' });
                          }}
                        >
                          <AlignRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {/* Size controls for rect, circle, placeholders */}
                {(selectedElement.type === 'rect' || selectedElement.type === 'logoPlaceholder' || selectedElement.type === 'photoPlaceholder' || selectedElement.type === 'qrCodePlaceholder' || selectedElement.type === 'headshotPlaceholder') && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.width || 100)}
                        onChange={(e) => updateSelectedElement({ width: parseInt(e.target.value) || 100 })}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.height || 100)}
                        onChange={(e) => updateSelectedElement({ height: parseInt(e.target.value) || 100 })}
                        className="h-9"
                      />
                    </div>
                  </div>
                )}

                {/* Preview Image for Placeholders */}
                {(selectedElement.type === 'logoPlaceholder' || selectedElement.type === 'photoPlaceholder' || selectedElement.type === 'headshotPlaceholder') && (
                  <div className="space-y-2">
                    <Label className="text-xs">Preview Image (for design review)</Label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        id="placeholder-preview-upload"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              updateSelectedElement({ previewImage: event.target.result });
                              toast.success('Preview image added');
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => document.getElementById('placeholder-preview-upload').click()}
                        data-testid="upload-preview-btn"
                      >
                        <Image className="h-4 w-4 mr-1" />
                        {selectedElement.previewImage ? 'Change' : 'Add'} Preview
                      </Button>
                      {selectedElement.previewImage && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            updateSelectedElement({ previewImage: null });
                            toast.success('Preview image removed');
                          }}
                          data-testid="remove-preview-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add a sample image to preview how the template will look. Users will replace this with their own images.
                    </p>
                  </div>
                )}

                {selectedElement.type === 'circle' && (
                  <div>
                    <Label className="text-xs">Radius</Label>
                    <Input
                      type="number"
                      value={Math.round(selectedElement.radius || 50)}
                      onChange={(e) => updateSelectedElement({ radius: parseInt(e.target.value) || 50 })}
                      className="h-9"
                    />
                  </div>
                )}

                {selectedElement.type === 'image' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.width || 100)}
                        onChange={(e) => updateSelectedElement({ width: parseInt(e.target.value) || 100 })}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input
                        type="number"
                        value={Math.round(selectedElement.height || 100)}
                        onChange={(e) => updateSelectedElement({ height: parseInt(e.target.value) || 100 })}
                        className="h-9"
                      />
                    </div>
                  </div>
                )}

                {/* Color control - not for newsletter blocks */}
                {selectedElement.type !== 'newsletterBlock' && selectedElement.type !== 'image' && (
                  <div>
                    <Label className="text-xs">Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={selectedColor}
                        onChange={(e) => {
                          setSelectedColor(e.target.value);
                          updateSelectedElement({ fill: e.target.value, stroke: e.target.value, backgroundColor: e.target.value });
                        }}
                        className="w-12 h-9"
                      />
                      <Input
                        value={selectedColor}
                        onChange={(e) => {
                          setSelectedColor(e.target.value);
                          updateSelectedElement({ fill: e.target.value, stroke: e.target.value, backgroundColor: e.target.value });
                        }}
                        className="h-9"
                      />
                    </div>
                  </div>
                )}

                {/* Opacity control */}
                {selectedElement.type !== 'newsletterBlock' && (
                  <div>
                    <Label className="text-xs">Opacity: {Math.round((selectedElement.opacity || 1) * 100)}%</Label>
                    <Slider
                      value={[selectedElement.opacity || 1]}
                      onValueChange={([val]) => {
                        setOpacity(val);
                        updateSelectedElement({ opacity: val });
                      }}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={duplicateElement} className="flex-1">
                    <Copy className="h-4 w-4 mr-1" />
                    Duplicate
                  </Button>
                  <Button variant="destructive" size="sm" onClick={deleteSelected} className="flex-1">
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Canvas Settings */}
          <div className="p-6 border-b border-border/50">
            <h3 className="font-semibold mb-4">Canvas</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={selectedBgColor}
                    onChange={(e) => setSelectedBgColor(e.target.value)}
                    className="w-12 h-9"
                  />
                  <Input
                    value={selectedBgColor}
                    onChange={(e) => setSelectedBgColor(e.target.value)}
                    className="h-9"
                  />
                </div>
              </div>

              {/* Grid toggle */}
              <div className="flex items-center justify-between">
                <Label className="text-xs">Show Grid</Label>
                <Button
                  variant={showGrid ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowGrid(!showGrid)}
                  data-testid="toggle-grid-btn"
                >
                  <Grid3X3 className="h-4 w-4 mr-1" />
                  {showGrid ? 'On' : 'Off'}
                </Button>
              </div>

              {showGrid && (
                <div>
                  <Label className="text-xs">Grid Size: {gridSize}px</Label>
                  <Slider
                    value={[gridSize]}
                    onValueChange={([val]) => setGridSize(val)}
                    min={10}
                    max={100}
                    step={10}
                  />
                </div>
              )}

              <div>
                <Label className="text-xs">Zoom: {Math.round(scale * 100)}%</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setScale(Math.max(0.1, scale - 0.1))}>
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setScale(Math.min(1, scale + 0.1))}>
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="p-6">
            <div className="space-y-2">
              <Button className="w-full" onClick={saveTemplate} disabled={saving} data-testid="save-template-btn">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Template'}
              </Button>
              <Button variant="outline" className="w-full" onClick={exportAsImage} data-testid="export-png-btn">
                <Download className="h-4 w-4 mr-2" />
                Export as PNG
              </Button>
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 flex bg-muted/20 overflow-hidden">
          {/* Vertical Page Scroll Panel (Print only) */}
          {isMultiPage && (
            <div className="w-[140px] flex-shrink-0 border-r border-border/50 bg-background/60 backdrop-blur-sm flex flex-col" data-testid="page-nav">
              <div className="p-2 border-b border-border/30 flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground">
                  {pages.length} {type === 'carousel' ? 'slide' : 'page'}{pages.length !== 1 ? 's' : ''}
                </span>
                <Button variant="ghost" size="sm" onClick={addPage} className="h-6 w-6 p-0" data-testid="add-page-btn" title="Add Page">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {type === 'postcard' && (
                <div className="px-2 py-1.5 border-b border-border/30 space-y-1.5">
                  <Select value={postcardSize} onValueChange={(val) => {
                    setPostcardSize(val);
                    if (val === 'custom') {
                      // Initialize custom inputs from the current canvas size
                      const w = Math.max(1, Math.round((canvasWidth / POSTCARD_DPI) * 10) / 10);
                      const h = Math.max(1, Math.round((canvasHeight / POSTCARD_DPI) * 10) / 10);
                      setPostcardCustomW(w);
                      setPostcardCustomH(h);
                      // Don't change canvas size yet — wait for user input
                      return;
                    }
                    const sz = POSTCARD_SIZES[val];
                    if (sz && sz.width && sz.height) { setCanvasWidth(sz.width); setCanvasHeight(sz.height); }
                  }}>
                    <SelectTrigger className="h-7 text-[10px]" data-testid="postcard-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(POSTCARD_SIZES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {postcardSize === 'custom' && (
                    <div className="flex items-center gap-1.5" data-testid="postcard-custom-inputs">
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          step="0.25"
                          value={postcardCustomW}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setPostcardCustomW(v);
                            if (v > 0) setCanvasWidth(inchesToPx(v));
                          }}
                          className="h-7 text-[10px] px-1.5"
                          placeholder="W"
                          data-testid="postcard-custom-w"
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground">×</span>
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="1"
                          max="24"
                          step="0.25"
                          value={postcardCustomH}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setPostcardCustomH(v);
                            if (v > 0) setCanvasHeight(inchesToPx(v));
                          }}
                          className="h-7 text-[10px] px-1.5"
                          placeholder="H"
                          data-testid="postcard-custom-h"
                        />
                      </div>
                      <span className="text-[9px] text-muted-foreground whitespace-nowrap">in</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {pages.map((page, idx) => (
                  <div
                    key={page.id}
                    onClick={() => switchToPage(idx)}
                    className={`group relative rounded-md cursor-pointer border-2 transition-all ${
                      currentPageIndex === idx
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-transparent hover:border-muted-foreground/20 hover:bg-muted/30'
                    }`}
                    data-testid={`page-btn-${idx}`}
                  >
                    <div
                      className="w-full rounded-sm bg-white overflow-hidden relative"
                      style={{
                        aspectRatio: `${canvasWidth} / ${canvasHeight}`,
                        backgroundColor: page.backgroundColor || '#ffffff',
                      }}
                    >
                      {pageThumbnails[idx] && (
                        <img
                          src={pageThumbnails[idx]}
                          alt={`Page ${idx + 1}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          draggable={false}
                        />
                      )}
                    </div>
                    <div className="flex items-center justify-between px-1.5 py-1">
                      <span className={`text-[10px] font-medium ${currentPageIndex === idx ? 'text-primary' : 'text-muted-foreground'}`}>
                        {type === 'postcard' && idx < 2 ? (idx === 0 ? 'Front' : 'Back') : type === 'carousel' ? `Slide ${idx + 1}` : `Page ${idx + 1}`}
                      </span>
                      {pages.length > 1 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePage(idx); }}
                          className="opacity-0 group-hover:opacity-100 w-4 h-4 bg-destructive/80 text-destructive-foreground rounded-full text-[9px] flex items-center justify-center hover:bg-destructive transition-opacity"
                          data-testid={`delete-page-${idx}`}
                        >
                          x
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <button
                  onClick={addPage}
                  className="w-full rounded-md border-2 border-dashed border-muted-foreground/20 hover:border-muted-foreground/40 py-3 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="add-page-btn-bottom"
                >
                  + Add Page
                </button>
              </div>
              <div className="p-2 border-t border-border/30">
                <span className="text-[10px] text-muted-foreground">{canvasWidth}×{canvasHeight}px</span>
              </div>
            </div>
          )}
          
          <div className="flex-1 flex flex-col items-center justify-center overflow-auto p-8">
          <div 
            className="border-2 border-border rounded-lg shadow-2xl" 
            style={{ 
              width: canvasWidth * scale, 
              height: canvasHeight * scale,
              backgroundColor: '#f8f8f8'
            }}
          >
            <Stage
              width={canvasWidth * scale}
              height={canvasHeight * scale}
              ref={stageRef}
              scaleX={scale}
              scaleY={scale}
              onMouseDown={checkDeselect}
              onTouchStart={checkDeselect}
            >
              <Layer>
                {/* Background */}
                <Rect
                  x={0}
                  y={0}
                  width={canvasWidth}
                  height={canvasHeight}
                  fill={selectedBgColor}
                  listening={false}
                />
                
                {/* Grid */}
                <GridLines width={canvasWidth} height={canvasHeight} gridSize={gridSize} show={showGrid} />
                
                {/* Elements */}
                {elements.map((element) => {
                  if (element.visible === false) return null;
                  
                  const isSelected = element.id === selectedId;

                  if (element.type === 'text') {
                    return (
                      <ResizableShape
                        key={element.id}
                        shapeProps={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                        shapeType="text"
                      />
                    );
                  } else if (element.type === 'rect') {
                    return (
                      <ResizableShape
                        key={element.id}
                        shapeProps={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                        shapeType="rect"
                      />
                    );
                  } else if (element.type === 'circle') {
                    return (
                      <ResizableShape
                        key={element.id}
                        shapeProps={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                        shapeType="circle"
                      />
                    );
                  } else if (element.type === 'line') {
                    return (
                      <ResizableShape
                        key={element.id}
                        shapeProps={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                        shapeType="line"
                      />
                    );
                  } else if (element.type === 'image') {
                    return (
                      <URLImage
                        key={element.id}
                        image={element}
                        x={element.x}
                        y={element.y}
                        width={element.width}
                        height={element.height}
                        draggable={!element.locked}
                        opacity={element.opacity}
                        locked={element.locked}
                        isLogo={element.isLogoFromBrandKit || element.originalType === 'logoPlaceholder'}
                        elementType={element.originalType || element.type}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, { ...element, ...newAttrs })}
                      />
                    );
                  } else if (element.type === 'logoPlaceholder' || element.type === 'photoPlaceholder' || element.type === 'qrCodePlaceholder' || element.type === 'headshotPlaceholder') {
                    return (
                      <PlaceholderShape
                        key={element.id}
                        element={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                      />
                    );
                  } else if (element.type === 'newsletterBlock') {
                    return (
                      <NewsletterBlockShape
                        key={element.id}
                        element={element}
                        isSelected={isSelected}
                        onSelect={() => handleElementClick(element.id)}
                        onChange={(newAttrs) => updateElement(element.id, newAttrs)}
                      />
                    );
                  } else if (element.type === 'photoGrid') {
                    return (
                      <React.Fragment key={element.id}>
                        <Group
                          x={element.x}
                          y={element.y}
                          draggable={!element.locked}
                          onClick={() => handleElementClick(element.id)}
                          onDragEnd={(e) => updateElement(element.id, { x: e.target.x(), y: e.target.y() })}
                        >
                          {(element.slots || []).map((slot, si) => (
                            <React.Fragment key={si}>
                              <Rect
                                x={slot.x - element.x}
                                y={slot.y - element.y}
                                width={slot.width}
                                height={slot.height}
                                fill="#E8E8E8"
                                stroke={isSelected ? '#3B82F6' : '#CCCCCC'}
                                strokeWidth={1}
                              />
                              <KonvaText
                                x={slot.x - element.x + slot.width / 2 - 20}
                                y={slot.y - element.y + slot.height / 2 - 6}
                                text={`Photo ${si + 1}`}
                                fontSize={10}
                                fill="#999"
                                listening={false}
                              />
                            </React.Fragment>
                          ))}
                        </Group>
                      </React.Fragment>
                    );
                  }
                  return null;
                })}
              </Layer>
            </Stage>
          </div>
        </div>
        </div>

        {/* Right Sidebar - Layers */}
        <div className="w-64 border-l border-border/50 bg-card overflow-y-auto">
          <div className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Layers ({elements.length})
            </h3>
            <div className="space-y-2">
              {[...elements].reverse().map((element) => (
                <div
                  key={element.id}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 ${
                    element.id === selectedId ? 'bg-primary/10 border-primary' : 'bg-muted/20'
                  }`}
                  onClick={() => handleElementClick(element.id)}
                  data-testid={`layer-${element.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium capitalize">
                      {element.type === 'newsletterBlock' ? element.blockType : element.type}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVisibility(element.id);
                        }}
                      >
                        {element.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLock(element.id);
                        }}
                      >
                        {element.locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                  {element.type === 'text' && (
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground truncate flex-1">{element.text}</p>
                      {element.isPropertyField && (
                        <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold" data-testid={`placeholder-tag-${element.id}`}>
                          {element.placeholderField || 'field'}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveLayer(element.id, 'up');
                      }}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveLayer(element.id, 'down');
                      }}
                    >
                      ↓
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            {elements.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-8">
                No elements yet.
                Add elements from the left panel.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
